"use client"

import { useState, type FormEvent, useEffect, useMemo, useCallback, type ChangeEvent } from "react"
import { observer } from "mobx-react-lite"
import { useAppContext } from "../../../../shared/functions/Context"
import { MAIL_EMAIL, USER_TEMPLATE_VIEW } from "../../../../shared/functions/mailMessages"
import type { IOption } from "../../../../shared/components/single-select/SingleSelect"
import showModalFromId, { hideModalFromId } from "../../../../shared/functions/ModalShow"
import MODAL_NAMES from "../../../dialogs/ModalName"
import Modal from "../../../../shared/components/Modal"
import RequestSubmittedAlertModal from "../../shared-components/RequestSubmittedModal"
import AdminQuestionnaireBox from "../components/AdminTemplateQuestionnaire"
import {
  type ITemplateRating,
  defaultTemplateRating,
} from "../../../../shared/models/three-sixty-feedback-models/TemplateRating"
import RequestTable from "../../requests/RequestsTable"
import {
  Plus,
  Minus,
  Send,
  Calendar,
  User,
  FileText,
  MessageSquare,
  Target,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Eye,
  Mail,
} from "lucide-react"

const UserTemplatesView = observer(() => {
  const { api, store } = useAppContext()
  const [selectedRaterId, setSelectedRaterId] = useState("")
  const [templateId, setTemplateId] = useState("")
  const [session, setSession] = useState<ITemplateRating>({
    ...defaultTemplateRating,
  })
  const [sentRequests, setSentRequests] = useState<ITemplateRating[]>([])
  const [ratedSessions, setRatedSessions] = useState<ITemplateRating[]>([])
  const [data, setData] = useState<ITemplateRating[]>([])
  const [isFormVisible, setIsFormVisible] = useState(false)
  const [isSendingEmail, setIsSendingEmail] = useState(false)
  const [showSuccessNotification, setShowSuccessNotification] = useState(false)
  const [showErrorNotification, setShowErrorNotification] = useState(false)

  const me = store.auth.meJson?.uid
  const users = store.user.all
  const allTemplates = store.templates.all.map((template) => template.asJson)

  const fetchData = useCallback(async () => {
    try {
      await api.templateRating.getAll()
      const allRatings = store.templateRating.all.map((rating) => rating.asJson)
      const filteredRatings = allRatings.filter((rating) => rating.rateeId === me)
      setData(filteredRatings)
    } catch (error) {
      console.error("Error fetching data:", error)
    }
  }, [api.templateRating, store.templateRating, me])

  useEffect(() => {
    const fetchInitialData = async () => {
      await api.user.getAll()
      await api.template.getAll()
      await api.ratingSession.getAll()
      fetchData()
    }
    fetchInitialData()
  }, [fetchData, api])

  const usersOptions: IOption[] = useMemo(
    () =>
      users.map((user) => ({
        label: user.asJson.displayName || "",
        value: user.asJson.uid,
      })),
    [users],
  )

  const templatesOptions: IOption[] = useMemo(
    () =>
      allTemplates.map((template) => ({
        label: template.title,
        value: template.id,
      })),
    [allTemplates],
  )

  const handleRequestRating = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (me && selectedRaterId && templateId) {
      const newSession: ITemplateRating = {
        ...session,
        rateeId: me || "",
        raterId: selectedRaterId,
        dateRequested: Date.now(),
        templateId: templateId,
        status: "Not Started",
        values: {},
        dueDate: dueDateValue,
      }

      try {
        setIsSendingEmail(true)
        await api.templateRating.create(newSession, selectedRaterId)
        const rater = users.find((user) => user.asJson.uid === selectedRaterId)
        if (!rater || !rater.asJson.email) {
          throw new Error("Rater not found or email is invalid")
        }

        const emailTemplate = USER_TEMPLATE_VIEW(
          session.heading,
          session.description,
          session.reasonForRequest,
          session.dueDate,
          templateId,
        )

        if (rater.asJson.email !== null) {
          await api.mail.sendMail([rater.asJson.email], MAIL_EMAIL, emailTemplate.MY_SUBJECT, emailTemplate.MY_BODY)
          setShowSuccessNotification(true)
          setTimeout(() => setShowSuccessNotification(false), 5000)
        }

        showModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.SUCCESSFULLY_REQUESTED_MODAL)
        setTimeout(() => {
          hideModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.SUCCESSFULLY_REQUESTED_MODAL)
        }, 3000)

        // Clear the session and form fields
        setSession(defaultTemplateRating)
        setTemplateId("")
        setSelectedRaterId("")
        setSentRequests([...sentRequests, newSession])
        setRatedSessions([...ratedSessions, newSession])
        fetchData()
      } catch (error) {
        console.error("Error requesting rating:", error)
        setShowErrorNotification(true)
        setTimeout(() => setShowErrorNotification(false), 5000)
      } finally {
        setIsSendingEmail(false)
      }
    }
    setIsFormVisible(false)
  }

  const [dueDateValue, setDueDateValue] = useState<number>(0)

  const handleDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    const date = new Date(event.target.value)
    setDueDateValue(date.valueOf())
    setSession({ ...session, dueDate: dueDateValue })
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <FileText className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">Template Requests</h2>
            <p className="text-slate-600">Request feedback using custom templates</p>
          </div>
        </div>

        {/* Toggle Form Button */}
        <button
          onClick={() => setIsFormVisible((prev) => !prev)}
          className={`flex items-center gap-3 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
            isFormVisible
              ? "bg-slate-200 text-slate-700 hover:bg-slate-300"
              : "bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-lg hover:shadow-xl"
          }`}
        >
          {isFormVisible ? <Minus className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
          {isFormVisible ? "Hide Request Form" : "Create New Request"}
        </button>
      </div>

      {/* Success/Error Notifications */}
      {showSuccessNotification && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3 animate-fadeIn">
          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
          <p className="text-green-800 text-sm font-medium">Email successfully sent!</p>
        </div>
      )}

      {showErrorNotification && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 animate-fadeIn">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-red-800 text-sm font-medium">Error sending email. Please try again.</p>
        </div>
      )}

      {/* Request Form */}
      {isFormVisible && (
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200/50 overflow-hidden">
          <div className="bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 p-6 border-b border-slate-200/50">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-800">Create Rating Request</h3>
                <p className="text-slate-600">Send a personalized feedback request to a colleague</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleRequestRating} className="p-6 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Heading */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <FileText className="h-4 w-4 text-purple-600" />
                  Request Heading <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={session.heading}
                  onChange={(e) => setSession({ ...session, heading: e.target.value })}
                  placeholder="Enter a clear heading for your request"
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                  required
                />
              </div>

              {/* Due Date */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <Calendar className="h-4 w-4 text-indigo-600" />
                  Due Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={dueDateValue ? new Date(dueDateValue).toISOString().substring(0, 10) : ""}
                  onChange={handleDateChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  required
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <MessageSquare className="h-4 w-4 text-blue-600" />
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={session.description}
                onChange={(e) => setSession({ ...session, description: e.target.value })}
                placeholder="Provide context and details about what you're looking for..."
                rows={4}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all duration-200"
                required
              />
            </div>

            {/* Reason for Request */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Target className="h-4 w-4 text-green-600" />
                Reason for Request <span className="text-red-500">*</span>
              </label>
              <textarea
                value={session.reasonForRequest}
                onChange={(e) => setSession({ ...session, reasonForRequest: e.target.value })}
                placeholder="Explain why you're requesting this feedback..."
                rows={4}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none transition-all duration-200"
                required
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Template Selection */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <FileText className="h-4 w-4 text-orange-600" />
                  Select Template <span className="text-red-500">*</span>
                </label>
                <select
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 appearance-none cursor-pointer"
                  required
                >
                  <option value="">Choose a template...</option>
                  {templatesOptions.map((template, index) => (
                    <option key={index} value={template.value}>
                      {template.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* User Selection */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <User className="h-4 w-4 text-teal-600" />
                  Select Rater <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedRaterId}
                  onChange={(e) => setSelectedRaterId(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200 appearance-none cursor-pointer"
                  required
                >
                  <option value="">Choose a user...</option>
                  {usersOptions.map((user, index) => (
                    <option key={index} value={user.value}>
                      {user.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Template Preview */}
            {templateId !== "" && (
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <Eye className="h-5 w-5 text-slate-600" />
                  <h4 className="text-lg font-semibold text-slate-800">Template Preview</h4>
                  <span className="px-3 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                    Read-Only
                  </span>
                </div>
                {allTemplates.map(
                  (template) =>
                    templateId === template.id && <AdminQuestionnaireBox key={template.id} template={template} />,
                )}
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={isSendingEmail}
                className={`flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 ${
                  isSendingEmail
                    ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                    : "bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-lg hover:shadow-xl hover:scale-105"
                }`}
              >
                {isSendingEmail ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Sending Request...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    Send Request
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Request Table */}
      {!isFormVisible && (
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200/50 overflow-hidden">
          <RequestTable data={data} onDataChange={fetchData} showArchivedButton={false} />
        </div>
      )}

      {/* Modal */}
      <Modal modalId={MODAL_NAMES.THREE_SIXTY_FEEDBACK.SUCCESSFULLY_REQUESTED_MODAL}>
        <RequestSubmittedAlertModal />
      </Modal>
    </div>
  )
})

export default UserTemplatesView
